/**
 * Helper per rendering e validazione template HSM Meta.
 *
 * Meta usa placeholder posizionali `{{1}}`, `{{2}}`, ... in body/header/buttons.
 * Quando l'operatore seleziona un template dalla UI, deve compilare le variabili
 * prima dell'invio. Questi helper isolano la logica di:
 *  - estrarre i placeholder da una stringa
 *  - sostituire i placeholder con i valori
 *  - identificare i placeholder rimasti vuoti (per UI warning)
 *  - estrarre testo da `components` array Meta-format
 *
 * Pattern ispirato a clara-chat ma adattato al formato Meta `{{N}}` (posizionale).
 */

/** Regex Meta: `{{1}}`, `{{2}}`, ... — gruppo 1 = indice numerico. */
export const META_VARIABLE_REGEX = /\{\{(\d+)\}\}/g;

export type TemplateVariables = Record<string, string>;

/**
 * Estrae gli indici dei placeholder in una stringa (es. ["1", "2", "1"] → unique ["1","2"]).
 * Ordine preservato.
 */
export function extractVariableIndexes(text: string): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(META_VARIABLE_REGEX.source, 'g');
  while ((m = re.exec(text)) !== null) {
    if (!seen.has(m[1])) {
      seen.add(m[1]);
      out.push(m[1]);
    }
  }
  return out;
}

/**
 * Sostituisce `{{N}}` con i valori da `variables`.
 * Variabili mancanti restano nel testo come `{{N}}` (segnale di mancata risoluzione).
 */
export function renderTemplate(text: string, variables: TemplateVariables): string {
  if (!text) return '';
  return text.replace(META_VARIABLE_REGEX, (match, idx: string) => {
    const v = variables[idx];
    return v && v.length > 0 ? v : match;
  });
}

/**
 * Ritorna gli indici delle variabili rimaste non risolte.
 * Usato in UI per warning rosso "X variabili da compilare prima dell'invio".
 */
export function getUnresolvedVariables(text: string, variables: TemplateVariables): string[] {
  return extractVariableIndexes(text).filter((idx) => !variables[idx] || variables[idx].length === 0);
}

/**
 * Estrae testo body / header / footer / buttons da un array `components` Meta-format.
 * Pattern Meta:
 *   { type: 'BODY' | 'HEADER' | 'FOOTER' | 'BUTTONS', text?: string, ... }
 *
 * Per HEADER con format=TEXT ritorna `text`. Per format=IMAGE/VIDEO/DOCUMENT
 * ritorna stringa vuota (l'header è multimediale, va popolato via attachment).
 */
export interface ExtractedTemplateText {
  header: string;
  body: string;
  footer: string;
  buttons: string[];
  headerFormat: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | null;
}

export function extractTemplateText(components: unknown[]): ExtractedTemplateText {
  const out: ExtractedTemplateText = {
    header: '',
    body: '',
    footer: '',
    buttons: [],
    headerFormat: null,
  };
  if (!Array.isArray(components)) return out;

  for (const c of components) {
    if (!c || typeof c !== 'object') continue;
    const comp = c as Record<string, unknown>;
    const type = String(comp.type || '').toUpperCase();

    if (type === 'HEADER') {
      const format = String(comp.format || 'TEXT').toUpperCase();
      out.headerFormat = (['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].includes(format) ? format : 'TEXT') as ExtractedTemplateText['headerFormat'];
      if (format === 'TEXT' && typeof comp.text === 'string') out.header = comp.text;
    } else if (type === 'BODY' && typeof comp.text === 'string') {
      out.body = comp.text;
    } else if (type === 'FOOTER' && typeof comp.text === 'string') {
      out.footer = comp.text;
    } else if (type === 'BUTTONS' && Array.isArray(comp.buttons)) {
      for (const btn of comp.buttons) {
        if (btn && typeof btn === 'object' && typeof (btn as { text?: unknown }).text === 'string') {
          out.buttons.push((btn as { text: string }).text);
        }
      }
    }
  }
  return out;
}

/**
 * Verifica se tutti i placeholder di un template sono risolti dalle variabili fornite.
 * Usato come precondizione invio (button "Invia" disabled se false).
 */
export function isTemplateReady(parts: ExtractedTemplateText, variables: TemplateVariables): boolean {
  const all = [parts.header, parts.body, parts.footer].join('\n');
  return getUnresolvedVariables(all, variables).length === 0;
}
